/* RESPONSIBLE TEAM: team-phone */
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import type Store from '@ember-data/store';
import type { FinVoiceNumberConfigurationHandoverConfiguration } from 'embercom/models/fin-voice/number-configuration';
import type { FinVoiceVoice } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';

const ASSIGN_CONVERSATION_STEP_TYPE = 'operator/visual-builder/step/assign-conversation';

export type OfficeHoursScheduleOption = {
  value: string;
  text: string;
  description: string;
  isDefault: boolean;
};

type NumberConfigurationUpdate = {
  appId: string;
  workspacePhoneNumber: string;
  predicates: {
    percentageRollout: number | undefined | null;
    officeHoursComparison: 'in' | 'nin' | undefined;
    officeHoursSchedule: string | undefined;
  };
  handoverConfiguration: FinVoiceNumberConfigurationHandoverConfiguration;
  status: 'active' | 'inactive';
  voice?: FinVoiceVoice;
  greeting?: string;
};

export default class FinVoiceConfigService extends Service {
  @service declare appService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare store: Store;

  @tracked numberConfigurations: Record<string, FinVoiceNumberConfiguration> = {};
  @tracked officeHoursSchedules: OfficeHoursScheduleOption[] = [];
  @tracked numberConfigurationsLoaded = false;

  getDefaultOfficeHoursSchedule() {
    return this.officeHoursSchedules.find((schedule) => schedule.isDefault)?.value;
  }

  async loadAllData() {
    if (this.numberConfigurationsLoaded) {
      return;
    }

    await Promise.all([this.loadNumberConfigurations(), this.loadOfficeHoursSchedules()]);
    this.numberConfigurationsLoaded = true;
  }

  async loadNumberConfigurations() {
    let numberConfigurations = await this.store.findAll('fin-voice/number-configuration');

    this.numberConfigurations = numberConfigurations.reduce(
      (acc, config) => {
        acc[config.workspacePhoneNumber] = config;
        return acc;
      },
      {} as Record<string, FinVoiceNumberConfiguration>,
    );
  }

  async loadOfficeHoursSchedules() {
    let officeHoursSchedules = (await this.store.findAll('office-hours-schedule')).toArray();

    this.officeHoursSchedules = officeHoursSchedules.map((schedule) => ({
      value: schedule.id,
      text: schedule.name,
      description: schedule.labelWithTimezone,
      isDefault: schedule.isDefault,
    }));
  }

  // This updates the entire configuration, once we fully work with number configurations, we should
  // update the configuration for a specific phone number instead of the entire configuration.
  async updateNumberConfigurations(
    predicates: any[],
    matchPercentage: number | undefined | null,
    genericTriageLocalizations: any[] | undefined,
    voice: FinVoiceVoice | undefined,
    greeting: string | undefined,
  ) {
    let activePhoneNumber = predicates.find(
      (predicate) => predicate.attribute === 'conversation.custom_attribute.workspace_phone_number',
    )?.value;

    if (!activePhoneNumber) {
      console.warn('No phone number found in predicates, cannot update number configurations');
      return;
    }

    let percentageRollout = predicates.find(
      (predicate) => predicate.attribute === 'random_number',
    )?.value;

    if (percentageRollout === undefined || percentageRollout === null) {
      percentageRollout = matchPercentage;
    }

    let officeHoursComparison = undefined;
    let officeHoursSchedule = this.getDefaultOfficeHoursSchedule();

    let officeHoursPredicate = predicates.find(
      (predicate) => predicate.attribute === 'office_hours.schedule',
    );

    if (officeHoursPredicate) {
      officeHoursComparison = officeHoursPredicate.comparison;
      officeHoursSchedule = officeHoursPredicate.value;
    }

    let numberConfiguration = this.numberConfigurations[activePhoneNumber];

    let handoverConfiguration = this.formatHandoverConfiguration(
      genericTriageLocalizations,
      officeHoursComparison,
    );

    if (!numberConfiguration) {
      // We only support one number configuration per phone number at the moment.
      await this.deleteNumberConfigurations();

      let config: NumberConfigurationUpdate = {
        appId: this.appService.app.id,
        workspacePhoneNumber: activePhoneNumber,
        predicates: {
          percentageRollout,
          officeHoursComparison,
          officeHoursSchedule,
        },
        handoverConfiguration,
        status: 'active',
      };

      if (voice) {
        config.voice = voice;
      }

      if (greeting) {
        config.greeting = greeting;
      }

      let newNumberConfiguration = this.store.createRecord(
        'fin-voice/number-configuration',
        config,
      );
      await newNumberConfiguration.save();

      this.numberConfigurations = { [activePhoneNumber]: newNumberConfiguration };
    } else {
      numberConfiguration.predicates = {
        percentageRollout,
        officeHoursComparison,
        officeHoursSchedule,
      };

      numberConfiguration.handoverConfiguration = handoverConfiguration;

      if (voice) {
        numberConfiguration.voice = voice;
      }

      if (greeting) {
        numberConfiguration.greeting = greeting;
      }

      await numberConfiguration.save();
    }
  }

  async deleteNumberConfigurations() {
    await Promise.all(
      Object.values(this.numberConfigurations).map((numberConfiguration) => {
        numberConfiguration.destroyRecord();
      }),
    );

    this.numberConfigurations = {};
  }

  formatHandoverConfiguration(
    genericTriageLocalizations: any[] | undefined,
    officeHoursComparison: 'in' | 'nin' | undefined,
  ): FinVoiceNumberConfigurationHandoverConfiguration {
    let step = genericTriageLocalizations?.firstObject?.visualBuilderObject.groups.find(
      (group: any) => group.isStart,
    )?.steps.firstObject;

    if (step?.type === ASSIGN_CONVERSATION_STEP_TYPE) {
      let assignee = step.action?.actionData?.assignee_id;

      if (officeHoursComparison) {
        return {
          [officeHoursComparison]: {
            type: 'handover-to-voicemail',
            config: {
              assigneeId: assignee,
            },
          },
        };
      }

      return {
        default: {
          type: 'handover-to-voicemail',
          config: {
            assigneeId: assignee,
          },
        },
      };
    }

    return {};
  }
}

declare module '@ember/service' {
  interface Registry {
    finVoiceConfigService: FinVoiceConfigService;
    'fin-voice-config-service': FinVoiceConfigService;
  }
}
